'use client';

import { useState } from 'react';
import { Check, Copy, Loader2, Share2 } from 'lucide-react';
import { ImagePreviewModal } from './image-preview-modal';

export interface IdeaDetailScreenData {
  id: string;
  prompt: string;
  imageUrl: string;
  model: {
    slug: string;
    name: string;
  };
}

interface IdeaDetailScreenProps {
  idea?: IdeaDetailScreenData | null;
  isLoading?: boolean;
  hasError?: boolean;
  copied?: boolean;
  onBack: () => void;
  onShare: () => void;
  onRepeat: () => void;
  onCopyPrompt: () => void;
}

export function IdeaDetailScreen({
  idea,
  isLoading = false,
  hasError = false,
  copied = false,
  onBack,
  onShare,
  onRepeat,
  onCopyPrompt,
}: IdeaDetailScreenProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (hasError || !idea) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4">
        <p className="text-center text-muted-foreground">Идея не найдена</p>
        <button
          onClick={onBack}
          className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Назад
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="relative z-10 flex h-[50px] items-center justify-between bg-background px-4">
        <button onClick={onBack} className="detail-back-button flex h-9 w-9 items-center justify-center rounded-full bg-[#242424]/40">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#fafafa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4L5 10L11 16" />
            <line x1="5" y1="10" x2="16" y2="10" />
          </svg>
        </button>
        <div className="flex items-center gap-2.5">
          <button onClick={onShare} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424]/40">
            <Share2 className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      <div className="relative w-full bg-background">
        {!mediaLoaded && (
          <div className="absolute inset-0 z-[5] flex min-h-[300px] items-center justify-center">
            <Loader2 className="h-16 w-16 animate-spin text-white" />
          </div>
        )}
        <div className="relative w-full cursor-pointer" onClick={() => setIsPreviewOpen(true)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={idea.imageUrl}
            alt={idea.prompt}
            className={`h-auto w-full transition-opacity duration-300 ${mediaLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setMediaLoaded(true)}
            onError={() => setMediaLoaded(true)}
          />
        </div>
      </div>

      <ImagePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        src={idea.imageUrl}
        alt={idea.prompt}
      />

      <div className="sticky bottom-9 z-20 px-4 pb-2.5 pt-5">
        <button
          onClick={onRepeat}
          className="flex h-14 w-full items-center justify-center rounded-[20px]"
          style={{
            background: 'linear-gradient(180deg, #7bc034 0%, #9DD94A 100%)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '-1px 3px 5.25px rgba(0,0,0,0.08), inset 0 1px 0 0 rgba(255,255,255,0.15)',
          }}
        >
          <span className="font-benzin text-base font-semibold text-white">ПОВТОРИТЬ</span>
        </button>
      </div>

      <div className="px-4 pt-4">
        <div className="overflow-hidden rounded-xl bg-white/5">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#8e8e93]">Промпт</p>
              <button
                onClick={onCopyPrompt}
                className="flex items-center gap-1 text-xs text-[#8e8e93]"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 text-green-500" /> Скопировано
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" /> Копировать
                  </>
                )}
              </button>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-[#e7e8ea]">{idea.prompt}</p>
          </div>

          <div className="mx-4 h-px bg-[#4e4e50]/50" />

          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-xs text-[#8e8e93]">Модель</p>
            <p className="text-sm font-semibold text-[#e7e8ea]">{idea.model.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
